import { useQuery } from "@apollo/client";
import React, { createContext, useContext } from "react";
import { graphql } from "~/gql";
import { Taxonomy, TaxonomyKey } from "~/gql/graphql";

export const TAXONOMY_QUERY = graphql(/* GraphQL */ `
  query Taxonomy {
    taxonomy {
      id
      createdAt
      updatedAt
      key
      value
    }
  }
`);

export type TaxonomyKeyValueType = Pick<Taxonomy, "key" | "value">;

const DEFAULT_TAXONOMY: TaxonomyKeyValueType[] = [
  { key: TaxonomyKey.FeeEarner, value: "Fee Earner" },
  { key: TaxonomyKey.PracticeArea, value: "Practice Area" },
  { key: TaxonomyKey.FeeEarnerLevel, value: "Fee Earner Level" },
  { key: TaxonomyKey.Template, value: "Template" },
];

export const ToHumanReadable = (key: TaxonomyKey) => {
  return DEFAULT_TAXONOMY.find((x) => x.key === key)?.value || key;
};

export const TaxonomyContext = createContext<Record<TaxonomyKey, string>>(
  DEFAULT_TAXONOMY.reduce((acc, curr) => {
    acc[curr.key] = curr.value;
    return acc;
  }, {} as Record<TaxonomyKey, string>)
);

export const TaxonomyProvider = ({ children }: { children: React.ReactNode }) => {
  const { data } = useQuery(TAXONOMY_QUERY);
  let taxonomy: Record<TaxonomyKey, string> = DEFAULT_TAXONOMY.reduce((acc, curr) => {
    acc[curr.key] = curr.value;
    return acc;
  }, {} as Record<TaxonomyKey, string>);

  if (data?.taxonomy.length) {
    taxonomy = data.taxonomy.reduce((acc, curr) => {
      acc[curr.key] = curr.value;
      return acc;
    }, {} as Record<TaxonomyKey, string>);
  }

  return <TaxonomyContext.Provider value={taxonomy}>{children}</TaxonomyContext.Provider>;
};

export const TaxonomyConsumer = TaxonomyContext.Consumer;

type LabelOptions = {
  textCase?: "lower" | "upper" | "title" | "capitalise";
  plural?: boolean;
};

export const VPDLabel = (label: keyof typeof TaxonomyKey, options?: LabelOptions) => {
  const taxonomy = useContext(TaxonomyContext);
  const taxonomyValue = taxonomy[label];
  let formattedLabel = taxonomyValue || label;
  if (options?.textCase === "lower") {
    formattedLabel = formattedLabel.toLowerCase();
  } else if (options?.textCase === "upper") {
    formattedLabel = formattedLabel.toUpperCase();
  } else if (options?.textCase === "title") {
    formattedLabel = formattedLabel.charAt(0).toUpperCase() + formattedLabel.slice(1);
  } else if (options?.textCase === "capitalise") {
    formattedLabel = formattedLabel
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  if (options?.plural) {
    return formattedLabel + "s";
  }

  return formattedLabel;
};

export const useTaxonomyLabel = () => {
  const taxonomy = useContext(TaxonomyContext);
  return taxonomy;
};
